import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  writeBatch,
  setDoc,
  limit,
  arrayUnion
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './useAuth';

export const useFirestore = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const MAX_BATCH_SIZE = 400;

  const chunkArray = (items, size = MAX_BATCH_SIZE) => {
    if (!Array.isArray(items) || items.length === 0) {
      return [];
    }
    const chunks = [];
    for (let i = 0; i < items.length; i += size) {
      chunks.push(items.slice(i, i + size));
    }
    return chunks;
  };

  const normalizeDocumentId = (value, fallbackId) => {
    if (typeof value === 'string' && value.trim() !== '') {
      return value.trim();
    }
    if (typeof value === 'number' || typeof value === 'bigint') {
      return String(value);
    }
    if (value instanceof Date) {
      return value.toISOString();
    }
    if (typeof fallbackId === 'string' && fallbackId.trim() !== '') {
      return fallbackId.trim();
    }
    return undefined;
  };

  // Funzione per ottenere il riferimento alla collezione dell'utente
  const getUserCollection = useCallback((collectionName) => {
    if (!user) {
      console.error('Utente non autenticato');
      throw new Error('Utente non autenticato');
    }
    return collection(db, 'users', user.uid, collectionName);
  }, [user]);

  // Funzione per ottenere il documento delle impostazioni dell'utente
  const getUserSettingsDocRef = useCallback(() => {
    if (!user) {
      console.error('Utente non autenticato');
      throw new Error('Utente non autenticato');
    }
    return doc(db, 'users', user.uid, 'settings', 'preferences');
  }, [user]);

  const clearCollection = useCallback(async (collectionName) => {
    const snapshot = await getDocs(getUserCollection(collectionName));
    if (snapshot.empty) {
      return;
    }

    const chunks = chunkArray(snapshot.docs);
    for (const docsChunk of chunks) {
      const batch = writeBatch(db);
      docsChunk.forEach(docSnapshot => {
        batch.delete(docSnapshot.ref);
      });
      await batch.commit();
    }
  }, [getUserCollection]);

  const writeDocuments = useCallback(async (collectionName, documents, options = {}) => {
    const { applyDefaultTimestamps = false, skipExistingIds = null } = options;

    if (!Array.isArray(documents) || documents.length === 0) {
      return;
    }

    const nowIso = new Date().toISOString();
    const chunks = chunkArray(documents);

    for (const docsChunk of chunks) {
      const batch = writeBatch(db);

      docsChunk.forEach(docData => {
        if (!docData || typeof docData !== 'object') {
          return;
        }

        const { id, documentId, ...rest } = docData;
        const docId = normalizeDocumentId(id ?? documentId);

        if (skipExistingIds && docId && skipExistingIds.has(docId)) {
          return;
        }

        const collectionRef = getUserCollection(collectionName);
        const docRef = docId ? doc(collectionRef, docId) : doc(collectionRef);

        const payload = { ...rest };
        if (Object.prototype.hasOwnProperty.call(payload, '_type')) {
          delete payload._type;
        }
        if (applyDefaultTimestamps) {
          if (!payload.createdAt) {
            payload.createdAt = nowIso;
          }
          if (!payload.updatedAt) {
            payload.updatedAt = nowIso;
          }
        }

        batch.set(docRef, payload);

        if (skipExistingIds && docId) {
          skipExistingIds.add(docId);
        }
      });

      await batch.commit();
    }
  }, [getUserCollection]);

  const deleteDocumentsById = useCallback(async (collectionName, ids = []) => {
    if (!Array.isArray(ids) || ids.length === 0) {
      return;
    }

    const validIds = ids
      .map(id => normalizeDocumentId(id))
      .filter(Boolean);

    if (validIds.length === 0) {
      return;
    }

    const chunks = chunkArray(validIds);
    for (const idsChunk of chunks) {
      const batch = writeBatch(db);
      idsChunk.forEach(id => {
        const docRef = doc(getUserCollection(collectionName), id);
        batch.delete(docRef);
      });
      await batch.commit();
    }
  }, [getUserCollection]);

  const loadUserSettings = useCallback(async () => {
    try {
      if (!user) {
        return null;
      }

      const docRef = getUserSettingsDocRef();
      const snapshot = await getDoc(docRef);
      if (snapshot.exists()) {
        return snapshot.data();
      }
      return null;
    } catch (error) {
      console.error('Errore durante il caricamento delle impostazioni utente:', error);
      setError('Errore durante il caricamento delle impostazioni: ' + error.message);
      throw error;
    }
  }, [user, getUserSettingsDocRef]);

  const saveUserSettings = useCallback(async (settings) => {
    try {
      if (!user) {
        throw new Error('Utente non autenticato');
      }

      setLoading(true);
      setError(null);
      const docRef = getUserSettingsDocRef();
      await setDoc(docRef, {
        ...settings,
        updatedAt: new Date().toISOString()
      }, { merge: true });
    } catch (error) {
      console.error('Errore durante il salvataggio delle impostazioni utente:', error);
      setError('Errore durante il salvataggio delle impostazioni: ' + error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user, getUserSettingsDocRef]);

  // Aggiungi documento
  const addDocument = useCallback(async (collectionName, document, forceUpdateStoresState) => {
    try {
      setLoading(true);
      setError(null);
      const collectionRef = getUserCollection(collectionName);
      const providedId = normalizeDocumentId(document?.id ?? document?.documentId);
      const docRef = providedId ? doc(collectionRef, providedId) : doc(collectionRef);

      const nowIso = new Date().toISOString();
      const payload = { ...document };

      if (providedId) {
        payload.id = providedId;
      }

      if (Object.prototype.hasOwnProperty.call(payload, 'documentId')) {
        delete payload.documentId;
      }

      await setDoc(docRef, {
        ...payload,
        createdAt: payload.createdAt || nowIso,
        updatedAt: nowIso
      });

      // Se è una spesa o entrata e contiene un nuovo negozio, aggiornalo con arrayUnion (molto più leggero di sanitizeStores)
      if ((collectionName === 'expenses' || collectionName === 'incomes') && document.store && document.store.trim()) {
        const storeName = document.store.trim();
        const storesRef = doc(db, 'users', user.uid, 'stores', 'default');

        // Usa arrayUnion che aggiunge solo se non esiste già, senza leggere tutto il DB
        await setDoc(storesRef, {
          stores: arrayUnion(storeName)
        }, { merge: true });
      }
    } catch (error) {
      setError('Errore durante l\'aggiunta: ' + error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [getUserCollection, user]);

  // Aggiorna documento
  const updateDocument = useCallback(async (collectionName, documentId, updates) => {
    try {
      setLoading(true);
      setError(null);

      const docRef = doc(db, 'users', user.uid, collectionName, documentId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      setError('Errore durante l\'aggiornamento: ' + error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Elimina documento
  const deleteDocument = useCallback(async (collectionName, documentId) => {
    try {
      setLoading(true);
      setError(null);

      const docRef = doc(db, 'users', user.uid, collectionName, documentId);
      await deleteDoc(docRef);
    } catch (error) {
      setError('Errore durante l\'eliminazione: ' + error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Elimina più documenti in batch
  const deleteMultipleDocuments = async (collectionName, documentIds) => {
    try {
      setLoading(true);
      setError(null);

      const batch = writeBatch(db);

      documentIds.forEach(id => {
        const docRef = doc(db, 'users', user.uid, collectionName, id);
        batch.delete(docRef);
      });

      await batch.commit();
    } catch (error) {
      setError('Errore durante l\'eliminazione multipla: ' + error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Carica tutti i dati dell'utente (per backup/restore)
  const loadAllUserData = async () => {
    try {
      if (!user) {
        throw new Error('Utente non autenticato');
      }

      setLoading(true);
      setError(null);

      const collectionsToExport = ['expenses', 'incomes', 'wallets', 'models', 'debts', 'recurringTransactions'];
      const exportedData = {
        expenses: [],
        incomes: [],
        wallets: [],
        models: [],
        debts: [],
        recurringTransactions: [],
        categories: [],
        stores: [],
        settings: null,
        completedEasterEggs: []
      };

      for (const collectionName of collectionsToExport) {
        const querySnapshot = await getDocs(getUserCollection(collectionName));
        exportedData[collectionName] = querySnapshot.docs.map(docSnapshot => ({
          id: docSnapshot.id,
          ...docSnapshot.data()
        }));
      }

      const categoriesSnapshot = await getDocs(getUserCollection('categories'));
      exportedData.categories = categoriesSnapshot.docs.map(docSnapshot => ({
        id: docSnapshot.id,
        ...docSnapshot.data()
      }));

      const storesSnapshot = await getDocs(getUserCollection('stores'));
      exportedData.stores = storesSnapshot.docs.map(docSnapshot => ({
        id: docSnapshot.id,
        ...docSnapshot.data()
      }));

      try {
        const settingsDocRef = getUserSettingsDocRef();
        const settingsSnapshot = await getDoc(settingsDocRef);
        if (settingsSnapshot.exists()) {
          exportedData.settings = {
            ...settingsSnapshot.data()
          };
        }
      } catch (settingsError) {
        console.error('Errore durante il recupero delle impostazioni per il backup:', settingsError);
      }

      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnapshot = await getDoc(userDocRef);
        if (userDocSnapshot.exists()) {
          const userData = userDocSnapshot.data();
          if (Array.isArray(userData.completedEasterEggs)) {
            exportedData.completedEasterEggs = [...userData.completedEasterEggs];
          }
        }
      } catch (easterEggError) {
        console.error('Errore durante il recupero degli easter egg completati per il backup:', easterEggError);
      }

      return exportedData;
    } catch (error) {
      setError('Errore durante il caricamento dati: ' + error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Importa dati (per backup/restore)
  const importUserData = async (rawData, callbacksOrFn) => {
    const callbacks = typeof callbacksOrFn === 'function'
      ? { onStoresUpdate: callbacksOrFn }
      : (callbacksOrFn || {});
    const { onStoresUpdate, onCategoriesUpdate } = callbacks;
    try {
      if (!user) {
        throw new Error('Utente non autenticato');
      }

      setLoading(true);
      setError(null);

      if (!rawData || typeof rawData !== 'object') {
        throw new Error('Formato dati di importazione non valido');
      }

      const normalizeArray = (value) => {
        if (!Array.isArray(value)) {
          return [];
        }
        return value.filter(item => item && typeof item === 'object');
      };

      const normalizeDocCollection = (value, options = {}) => {
        const { fallbackId = 'default', wrapField } = options;

        if (Array.isArray(value)) {
          const containsOnlyPrimitives = value.every(item => item === null || typeof item !== 'object');
          if (wrapField && containsOnlyPrimitives) {
            return value.length > 0 ? [{ id: fallbackId, [wrapField]: [...value] }] : [];
          }
          return value
            .filter(item => item && typeof item === 'object')
            .map((item, index) => {
              const { id, documentId, ...rest } = item;
              const fallbackValue = index === 0 ? fallbackId : `${fallbackId}_${index}`;
              return { id: id || documentId || fallbackValue, ...rest };
            });
        }

        if (value && typeof value === 'object') {
          const { id, documentId, ...rest } = value;
          return [{ id: id || documentId || fallbackId, ...rest }];
        }

        if (wrapField && Array.isArray(value)) {
          return value.length > 0 ? [{ id: fallbackId, [wrapField]: [...value] }] : [];
        }

        return [];
      };

      const extractStoresFromTransactions = (transactions) => {
        if (!Array.isArray(transactions)) {
          return [];
        }
        return transactions
          .filter(item => item && typeof item === 'object' && item.store && typeof item.store === 'string')
          .map(item => item.store.trim())
          .filter(Boolean);
      };

      const dataSection = rawData.data && typeof rawData.data === 'object' ? rawData.data : rawData;

      const validateTransaction = (item, isRecurring = false) => {
        return item &&
          typeof item === 'object' &&
          item.amount != null &&
          (isRecurring ? (item.nextOccurrence || item.date) : item.date) &&
          item.category;
      };

      const validateWallet = (item) => {
        return item &&
          typeof item === 'object' &&
          item.name;
      };

      const categoriesDataRaw = dataSection.categories || rawData.categories;
      const modelsDataRaw = dataSection.models || rawData.models;
      const storesDataRaw = dataSection.stores || rawData.stores;

      const normalizedData = {
        expenses: normalizeArray(dataSection.expenses || rawData.expenses).filter(item => validateTransaction(item)),
        incomes: normalizeArray(dataSection.incomes || rawData.incomes).filter(item => validateTransaction(item)),
        wallets: normalizeArray(dataSection.wallets || rawData.wallets).filter(validateWallet).map(w => ({
          ...w,
          initialBalance: w.initialBalance !== undefined ? w.initialBalance : (w.balance !== undefined ? w.balance : 0)
        })),
        debts: normalizeArray(dataSection.debts || rawData.debts).filter(item => item && item.amount != null && (item.personName || item.person)),
        recurringTransactions: normalizeArray(dataSection.recurringTransactions || rawData.recurringTransactions).filter(item => validateTransaction(item, true)),
        models: normalizeDocCollection(modelsDataRaw, { fallbackId: 'default' }),
        categories: normalizeDocCollection(categoriesDataRaw, { fallbackId: 'default' }),
        stores: normalizeDocCollection(
          storesDataRaw,
          { fallbackId: 'default', wrapField: 'stores' }
        ),
        settings: dataSection.settings && typeof dataSection.settings === 'object' ? dataSection.settings : (rawData.settings && typeof rawData.settings === 'object' ? rawData.settings : null),
        completedEasterEggs: Array.isArray(dataSection.completedEasterEggs || rawData.completedEasterEggs)
          ? (dataSection.completedEasterEggs || rawData.completedEasterEggs)
          : []
      };

      console.log('[importUserData] Normalized data summary:', {
        expenses: normalizedData.expenses.length,
        incomes: normalizedData.incomes.length,
        wallets: normalizedData.wallets.length,
        debts: normalizedData.debts.length,
        recurring: normalizedData.recurringTransactions.length,
        categories: normalizedData.categories.length,
        stores: normalizedData.stores.length
      });

      const extractedStores = [
        ...extractStoresFromTransactions(normalizedData.expenses),
        ...extractStoresFromTransactions(normalizedData.incomes)
      ];

      if (extractedStores.length > 0) {
        const existingStoresDoc = normalizedData.stores.find(docItem => Array.isArray(docItem?.stores));
        if (existingStoresDoc) {
          const merged = new Set(existingStoresDoc.stores);
          extractedStores.forEach(storeName => merged.add(storeName));
          existingStoresDoc.stores = Array.from(merged);
        } else {
          normalizedData.stores.push({
            id: 'default',
            stores: Array.from(new Set(extractedStores))
          });
        }
      }

      // Invalida la cache locale se esiste (opzionale, ma buona pratica)
      // Non chiamiamo sanitizeStores qui per evitare letture extra, ci fidiamo dei dati importati + logica sopra

      const mergeSimpleCollection = async (collectionName, documents, options = {}) => {
        if (!Array.isArray(documents) || documents.length === 0) {
          return;
        }
        const existingSnapshot = await getDocs(getUserCollection(collectionName));
        const existingIds = new Set(existingSnapshot.docs.map(docSnapshot => docSnapshot.id));
        await writeDocuments(collectionName, documents, {
          applyDefaultTimestamps: options.applyDefaultTimestamps === true,
          skipExistingIds: existingIds
        });
      };

      await mergeSimpleCollection('expenses', normalizedData.expenses, { applyDefaultTimestamps: true });
      await mergeSimpleCollection('incomes', normalizedData.incomes, { applyDefaultTimestamps: true });
      await mergeSimpleCollection('wallets', normalizedData.wallets, { applyDefaultTimestamps: true });
      await mergeSimpleCollection('debts', normalizedData.debts, { applyDefaultTimestamps: true });
      await mergeSimpleCollection('recurringTransactions', normalizedData.recurringTransactions, { applyDefaultTimestamps: true });

      const categoriesSnapshot = await getDocs(getUserCollection('categories'));
      const existingCategoryDocs = new Map();
      categoriesSnapshot.docs.forEach(docSnapshot => {
        existingCategoryDocs.set(docSnapshot.id, docSnapshot.data());
      });

      const mergeCategoryArray = (existingArray = [], incomingArray = []) => {
        const resultMap = new Map();
        
        // Add existing first
        existingArray.forEach(item => {
          if (!item || typeof item !== 'object') return;
          const nameKey = typeof item.name === 'string' ? item.name.trim().toLowerCase() : null;
          const key = item.id ?? nameKey ?? `existing_${resultMap.size}`;
          resultMap.set(key, item);
        });

        // Overwrite with incoming
        incomingArray.forEach(item => {
          if (!item || typeof item !== 'object') return;
          const nameKey = typeof item.name === 'string' ? item.name.trim().toLowerCase() : null;
          const key = item.id ?? nameKey ?? `incoming_${resultMap.size}`;
          resultMap.set(key, item);
        });

        return Array.from(resultMap.values());
      };

      const mergeModelsArray = mergeCategoryArray;

      // AGGREGATE all incoming category documents into one
      const aggregatedIncomingCategories = {
        expense: [],
        income: [],
        wallet: []
      };

      normalizedData.categories.forEach(docItem => {
        if (!docItem) return;
        
        // If it's a document containing arrays (current format)
        if (Array.isArray(docItem.expense)) aggregatedIncomingCategories.expense.push(...docItem.expense);
        if (Array.isArray(docItem.income)) aggregatedIncomingCategories.income.push(...docItem.income);
        if (Array.isArray(docItem.wallet)) aggregatedIncomingCategories.wallet.push(...docItem.wallet);
        
        // If the object itself is a category (older/alternative format)
        if (docItem.name && docItem.type) {
          if (docItem.type === 'expense') aggregatedIncomingCategories.expense.push(docItem);
          else if (docItem.type === 'income') aggregatedIncomingCategories.income.push(docItem);
          else if (docItem.type === 'wallet') aggregatedIncomingCategories.wallet.push(docItem);
        }
      });

      // We consolidate into a single 'default' document for the app
      const existingDoc = existingCategoryDocs.get('default') || existingCategoryDocs.values().next().value || {};
      const existingExpense = Array.isArray(existingDoc?.expense) ? existingDoc.expense : [];
      const existingIncome = Array.isArray(existingDoc?.income) ? existingDoc.income : [];
      const existingWallet = Array.isArray(existingDoc?.wallet) ? existingDoc.wallet : [];

      const mergedExpense = mergeCategoryArray(existingExpense, aggregatedIncomingCategories.expense);
      const mergedIncome = mergeCategoryArray(existingIncome, aggregatedIncomingCategories.income);
      const mergedWallet = mergeCategoryArray(existingWallet, aggregatedIncomingCategories.wallet);

      console.log(`[importUserData] Final consolidated categories:`, {
        expense: mergedExpense.length,
        income: mergedIncome.length,
        wallet: mergedWallet.length
      });

      const categoriesPayload = {
        ...existingDoc,
        expense: mergedExpense,
        income: mergedIncome,
        wallet: mergedWallet,
        updatedAt: new Date().toISOString()
      };
      delete categoriesPayload.id;
      delete categoriesPayload.documentId;

      await setDoc(doc(getUserCollection('categories'), 'default'), categoriesPayload);

      // Clean up other category documents if they exist
      const otherCategoryIds = Array.from(existingCategoryDocs.keys()).filter(id => id !== 'default');
      if (otherCategoryIds.length > 0) {
        await deleteDocumentsById('categories', otherCategoryIds);
      }

      // Repeat aggregation logic for models
      const modelsSnapshot = await getDocs(getUserCollection('models'));
      const existingModelDocs = new Map();
      modelsSnapshot.docs.forEach(docSnapshot => {
        existingModelDocs.set(docSnapshot.id, docSnapshot.data());
      });

      const aggregatedIncomingModels = [];
      normalizedData.models.forEach(docItem => {
        if (!docItem) return;
        if (Array.isArray(docItem.models)) aggregatedIncomingModels.push(...docItem.models);
        else if (docItem.name && docItem.amount) aggregatedIncomingModels.push(docItem); // Individual model
      });

      const existingModelDoc = existingModelDocs.get('default') || existingModelDocs.values().next().value || {};
      const mergedModels = mergeModelsArray(Array.isArray(existingModelDoc?.models) ? existingModelDoc.models : [], aggregatedIncomingModels);
      
      const modelsPayload = {
        ...existingModelDoc,
        models: mergedModels,
        updatedAt: new Date().toISOString()
      };
      delete modelsPayload.id;
      delete modelsPayload.documentId;

      await setDoc(doc(getUserCollection('models'), 'default'), modelsPayload);

      // Repeat aggregation logic for stores
      const storesSnapshot = await getDocs(getUserCollection('stores'));
      const existingStoresDocs = new Map();
      storesSnapshot.docs.forEach(docSnapshot => {
        existingStoresDocs.set(docSnapshot.id, docSnapshot.data());
      });

      const aggregatedIncomingStores = new Set();
      normalizedData.stores.forEach(docItem => {
        if (!docItem) return;
        if (Array.isArray(docItem.stores)) docItem.stores.forEach(s => typeof s === 'string' && aggregatedIncomingStores.add(s));
        else if (typeof docItem === 'string') aggregatedIncomingStores.add(docItem);
      });

      const existingStoresDoc = existingStoresDocs.get('default') || existingStoresDocs.values().next().value || {};
      const existingStoresList = Array.isArray(existingStoresDoc?.stores) ? existingStoresDoc.stores : [];
      existingStoresList.forEach(s => typeof s === 'string' && aggregatedIncomingStores.add(s));

      const storesPayload = {
        ...existingStoresDoc,
        stores: Array.from(aggregatedIncomingStores),
        updatedAt: new Date().toISOString()
      };
      delete storesPayload.id;
      delete storesPayload.documentId;

      await setDoc(doc(getUserCollection('stores'), 'default'), storesPayload);

      if (typeof onCategoriesUpdate === 'function') {
        onCategoriesUpdate({
          expense: mergedExpense,
          income: mergedIncome,
          wallet: mergedWallet
        });
      }

      if (normalizedData.settings) {
        try {
          const settingsDocRef = getUserSettingsDocRef();
          const existingSettingsSnapshot = await getDoc(settingsDocRef);
          const existingSettings = existingSettingsSnapshot.exists() ? existingSettingsSnapshot.data() : {};
          await setDoc(settingsDocRef, {
            ...existingSettings,
            ...normalizedData.settings,
            updatedAt: new Date().toISOString()
          }, { merge: true });
        } catch (settingsError) {
          console.error('Errore durante l\'aggiornamento delle impostazioni utente:', settingsError);
        }
      }

      if (Array.isArray(normalizedData.completedEasterEggs)) {
        try {
          const userDocRef = doc(db, 'users', user.uid);
          const userDocSnapshot = await getDoc(userDocRef);
          const existingEggs = userDocSnapshot.exists() && Array.isArray(userDocSnapshot.data().completedEasterEggs)
            ? userDocSnapshot.data().completedEasterEggs
            : [];
          const mergedEggs = Array.from(new Set([
            ...existingEggs,
            ...normalizedData.completedEasterEggs
          ]));
          await setDoc(userDocRef, {
            completedEasterEggs: mergedEggs,
            updatedAt: new Date().toISOString()
          }, { merge: true });
        } catch (eggError) {
          console.error('Errore durante l\'aggiornamento degli easter egg completati:', eggError);
        }
      }

      if (typeof onStoresUpdate === 'function') {
        onStoresUpdate(Array.from(aggregatedIncomingStores));
      }
    } catch (error) {
      setError('Errore durante l\'importazione: ' + error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Elimina tutti i dati dell'utente
  const deleteAllUserData = async () => {
    try {
      if (!user) {
        throw new Error('Utente non autenticato');
      }

      setLoading(true);
      setError(null);

      const collections = ['expenses', 'incomes', 'wallets', 'debts', 'recurringTransactions', 'models', 'categories', 'stores'];

      for (const collectionName of collections) {
        await clearCollection(collectionName);
      }

      try {
        await deleteDoc(getUserSettingsDocRef());
      } catch (settingsError) {
        console.error('Errore durante la rimozione delle impostazioni utente:', settingsError);
      }

      try {
        const userDocRef = doc(db, 'users', user.uid);
        await setDoc(userDocRef, {
          completedEasterEggs: [],
          updatedAt: new Date().toISOString()
        }, { merge: true });
      } catch (userDocError) {
        console.error('Errore durante il reset degli easter egg completati:', userDocError);
      }
    } catch (error) {
      setError('Errore durante l\'eliminazione di tutti i dati: ' + error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Gestione Easter Eggs completati
  const getCompletedEasterEggs = async () => {
    try {
      const userDoc = doc(db, 'users', user.uid);
      const userSnapshot = await getDoc(userDoc);

      if (userSnapshot.exists()) {
        return userSnapshot.data().completedEasterEggs || [];
      }
      return [];
    } catch (error) {
      console.error('Errore nel recupero easter egg completati:', error);
      return [];
    }
  };

  const setEasterEggCompleted = async (easterEggId) => {
    try {
      setLoading(true);
      setError(null);
      const userDoc = doc(db, 'users', user.uid);
      const userSnapshot = await getDoc(userDoc);
      let completedEasterEggs = [];
      if (userSnapshot.exists()) {
        completedEasterEggs = userSnapshot.data().completedEasterEggs || [];
      }
      if (!completedEasterEggs.includes(easterEggId)) {
        completedEasterEggs.push(easterEggId);
        // Usa setDoc con merge:true per creare il documento se non esiste
        await setDoc(userDoc, {
          completedEasterEggs,
          updatedAt: new Date().toISOString()
        }, { merge: true });
      }
    } catch (error) {
      setError('Errore nel salvataggio easter egg: ' + error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const isEasterEggCompleted = async (easterEggId) => {
    try {
      const completedEasterEggs = await getCompletedEasterEggs();
      return completedEasterEggs.includes(easterEggId);
    } catch (error) {
      console.error('Errore nel controllo easter egg:', error);
      return false;
    }
  };

  // Funzione per sanificare la lista dei negozi: assicura che tutti i negozi usati in expenses/incomes siano presenti in stores.
  // ATTENZIONE: Questa funzione legge TUTTO il database. Usare con cautela, solo su richiesta esplicita o importazione dati.
  const sanitizeStores = async (forceUpdateStoresState) => {
    try {
      setLoading(true);
      setError(null);

      // Carica tutte le spese e entrate
      const expensesSnap = await getDocs(getUserCollection('expenses'));
      const incomesSnap = await getDocs(getUserCollection('incomes'));
      const storesSet = new Set();

      expensesSnap.docs.forEach(doc => {
        const data = doc.data();
        if (data.store && data.store.trim()) {
          storesSet.add(data.store.trim());

        }
      });

      incomesSnap.docs.forEach(doc => {
        const data = doc.data();
        if (data.store && data.store.trim()) {
          storesSet.add(data.store.trim());

        }
      });
      const finalStores = Array.from(storesSet);

      // Salva la lista finale su Firestore (anche se vuota)
      const storesRef = doc(getUserCollection('stores'), 'default');
      await setDoc(storesRef, { stores: finalStores });

      if (typeof forceUpdateStoresState === 'function') forceUpdateStoresState(finalStores);
    } catch (error) {
      setError('Errore durante la sanificazione negozi: ' + error.message);
      console.error('[sanitizeStores] Errore:', error);
      throw error;
    } finally {
      setLoading(false);

    }
  };

  return {
    loading,
    error,
    useCollectionData,
    addDocument,
    updateDocument,
    deleteDocument,
    deleteMultipleDocuments,
    loadAllUserData,
    importUserData,
    deleteAllUserData,
    getCompletedEasterEggs,
    setEasterEggCompleted,
    isEasterEggCompleted,
    loadUserSettings,
    saveUserSettings,
    sanitizeStores,
    clearError: () => setError(null)
  };
};

// Carica dati in tempo reale
export const useCollectionData = (collectionName, orderByField = 'date', limitCount = null, whereConstraints = []) => {
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const userId = user?.uid;

  useEffect(() => {
    if (!userId) {
      setData([]);
      setLoading(false);
      return;
    }

    const getUserCollection = (name) => {
      return collection(db, 'users', userId, name);
    };

    const constraints = [];

    // Aggiungi vincoli where se presenti
    if (Array.isArray(whereConstraints) && whereConstraints.length > 0) {
      whereConstraints.forEach(constraint => {
        // Assicurati che il constraint sia valido (array di 3 elementi: [field, op, value])
        if (Array.isArray(constraint) && constraint.length === 3) {
          constraints.push(where(constraint[0], constraint[1], constraint[2]));
        }
      });
    }

    // Aggiungi ordinamento se richiesto
    if (orderByField) {
      constraints.push(orderBy(orderByField, 'asc'));
    }

    // Aggiungi limite se richiesto
    if (limitCount && Number.isInteger(limitCount) && limitCount > 0) {
      constraints.push(limit(limitCount));
    }

    const q = query(getUserCollection(collectionName), ...constraints);

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items = snapshot.docs.map(doc => {
        const data = doc.data();
        const hasOwnId = Object.prototype.hasOwnProperty.call(data, 'id') && data.id;
        return {
          ...data,
          id: hasOwnId ? data.id : doc.id,
          documentId: doc.id
        };
      });



      setData((prevData) => {
        // Prevent re-render if data is deeply equal
        if (JSON.stringify(prevData) === JSON.stringify(items)) {
          return prevData;
        }
        return items;
      });
      setLoading(false);
    }, (error) => {
      console.error('Errore nel caricamento dati per ' + collectionName + ':', error);
      setLoading(false);
    });

    return unsubscribe;
  }, [userId, collectionName, orderByField, limitCount, JSON.stringify(whereConstraints)]);

  return { data, loading };
};
